import { useEffect } from "react";
import type { ReactNode } from "react";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** largura máxima do painel */
  size?: "sm" | "md" | "lg";
  labelledBy?: string;
}

const sizes = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" } as const;

/**
 * Primitivo de sobreposição. Não usar diretamente nas telas:
 * `Dialog` e `ConfirmDialog` são construídos sobre este componente.
 */
export default function Modal({ open, onClose, children, size = "md", labelledBy }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`relative w-full ${sizes[size]} rounded-t-xl border border-line/70 bg-surface-raised p-5 shadow-2xl sm:rounded-xl`}
      >
        {children}
      </div>
    </div>
  );
}
